import {
  Divider,
  Form,
  PrimaryButton,
  Space,
  TertiaryButton,
} from '@procurify/ui'
import { FormikProvider, useFormik } from 'formik'
import * as Yup from 'yup'

import { useProcurifyIntl } from '@webapp/hooks'
import messages from './messages'

export interface POPDFLabelSetNameFormSchema {
  name: string
}

export interface IPOPDFLabelSetNameFormProps {
  /** Present in rename mode — pre-fills the name field. */
  initialName?: string
  /** Localized submit button text — the consuming presenter supplies it. */
  submitButtonText: string
  isSaving: boolean
  onSubmit: (values: POPDFLabelSetNameFormSchema) => void
  onCancel: () => void
}

/**
 * Shared prop-configured create/rename name form for PO PDF label sets
 * (ADR-9). Form content only — the ModalDialog shell is Feature-Set-owned.
 */
export const POPDFLabelSetNameForm = ({
  initialName,
  submitButtonText,
  isSaving,
  onSubmit,
  onCancel,
}: IPOPDFLabelSetNameFormProps) => {
  const intl = useProcurifyIntl()

  const formik = useFormik<POPDFLabelSetNameFormSchema>({
    initialValues: { name: initialName ?? '' },
    validationSchema: Yup.object({
      name: Yup.string()
        .trim()
        .required(intl.formatMessage(messages.nameRequiredError)),
    }),
    onSubmit: (values, { setSubmitting }) => {
      onSubmit({ name: values.name.trim() })
      // The mutation's isSaving is the in-flight indicator; failures keep
      // the form open, so Formik must not latch isSubmitting
      setSubmitting(false)
    },
  })

  return (
    <FormikProvider value={formik}>
      <Form.Content noValidate autoComplete='off'>
        <Space>
          <Form.TextField
            id='name'
            name='name'
            label={intl.formatMessage(messages.nameLabel)}
            placeholder={intl.formatMessage(messages.namePlaceholder)}
            required
            autoFocus
          />

          <Divider />
          <Space direction='horizontal' justify='end'>
            <TertiaryButton type='button' onClick={onCancel}>
              {intl.formatMessage(messages.cancelButton)}
            </TertiaryButton>
            <PrimaryButton type='submit' isLoading={isSaving}>
              {submitButtonText}
            </PrimaryButton>
          </Space>
        </Space>
      </Form.Content>
    </FormikProvider>
  )
}

POPDFLabelSetNameForm.displayName = 'POPDFLabelSetNameForm'
